/**
 * New Rent Increase Page
 * Planning form for a lease's rent increase (lease-lifecycle — The Rent Increase Process,
 * J-RI-INITIATE). The shared RentIncreaseForm shows a live plan preview (steps, due dates,
 * jurisdiction warnings, who will be emailed) BEFORE anything exists; "Create draft" then persists a
 * DRAFT RentIncrease and lands on RentIncreaseDetailPage. A draft sends nothing.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getLease, initiateRentIncrease, listRentIncreaseCandidates } from '../lib/api';
import { useUnitTimezone } from '../hooks/useUnitTimezone';
import { deriveTimezone } from '../utils/date';
import type { LeaseDetail } from '../types/lease';
import { PageHeader } from '../components/layout/index';
import { Skeleton } from '../components/ui/skeleton';
import { RentIncreaseForm, type RentIncreaseFormContext } from '../components/rent-increase/RentIncreaseForm';

export function NewRentIncreasePage() {
  const { leaseId } = useParams<{ leaseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentRentCents, setCurrentRentCents] = useState<number | null>(null);
  const [context, setContext] = useState<RentIncreaseFormContext | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [isLoadingLease, setIsLoadingLease] = useState(true);
  const propertyTimezone = useUnitTimezone(unitId).data ?? deriveTimezone('');

  // The Rent Increases list passes its origin in router state, so the back link and Cancel return there
  // instead of to the lease (the lease page is the other entry point).
  const cameFromList = (location.state as { from?: string } | null)?.from === 'rent-increases';

  useEffect(() => {
    if (!leaseId) return;
    let cancelled = false;
    (async () => {
      // Both lookups are context only: the form works (and the server stays the authority) without them.
      const [lease, candidates] = await Promise.all([
        // getLease's declared Promise<LeaseResponse> return type is stale (does not match the
        // flat-object runtime response) — LeaseDetailPage.tsx casts through `unknown` for the same reason.
        (getLease('', leaseId) as Promise<unknown>).then((l) => l as LeaseDetail).catch(() => null),
        listRentIncreaseCandidates().catch(() => null),
      ]);
      if (cancelled) return;
      const candidate = candidates?.candidates.find((c) => c.leaseId === leaseId);
      setCurrentRentCents(candidate?.currentRentAmountCents ?? lease?.rentAmount ?? null);
      setUnitId(lease?.unitId ?? candidate?.unit.id ?? null);
      setContext(
        candidate
          ? {
              propertyName: candidate.property.name,
              unitNumber: candidate.unit.unitNumber,
              tenantNames: candidate.tenants.map((t) => t.name),
            }
          : null
      );
      setIsLoadingLease(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [leaseId]);

  function handleCancel() {
    // `key === 'default'` means this page was the first entry (deep link): there is nothing to go back to.
    if (location.key !== 'default') navigate(-1);
    else navigate(cameFromList || !leaseId ? '/rent-increases' : `/leases/${leaseId}`);
  }

  if (!leaseId) return null;

  if (isLoadingLease) {
    return (
      <div className="max-w-5xl mx-auto pb-6 px-[var(--page-padding-x)] pt-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-24 w-full mt-4" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-6" data-testid="new-rent-increase-page">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Plan a rent increase"
          backLink={
            cameFromList
              ? { label: 'Rent Increases', to: '/rent-increases' }
              : { label: 'Back to Lease', to: `/leases/${leaseId}` }
          }
        />
      </div>

      <div className="px-[var(--page-padding-x)]">
        <RentIncreaseForm
          mode="create"
          leaseId={leaseId}
          currentRentCents={currentRentCents}
          context={context}
          unitId={unitId}
          timezone={propertyTimezone}
          onSubmit={async (values) => {
            const result = await initiateRentIncrease(leaseId, values);
            navigate(`/rent-increases/${result.id}`);
          }}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
}

export default NewRentIncreasePage;
